import * as React from "react";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/SearchInput";

export interface CheckboxGroupItem {
	id: string;
	name: string;
	checked: boolean;
}

export interface CheckboxGroup {
	name: string;
	items: CheckboxGroupItem[];
}

interface FilterableCheckboxGroupDropdownProps {
	groups: CheckboxGroup[];
	onCheckedChange: (id: any, checked: boolean) => void;
	onToggleAll?: (checked: boolean) => void;
	buttonContent: React.ReactNode;
	buttonVariant?: React.ComponentProps<typeof Button>["variant"];
	buttonSize?: React.ComponentProps<typeof Button>["size"];
	buttonClassName?: string;
	contentAlign?: React.ComponentProps<typeof DropdownMenuContent>["align"];
	contentClassName?: string;
	loading?: boolean;
	searchPlaceholder?: string;
	buttonLabel?: string;
	renderItem?: (item: CheckboxGroupItem) => React.ReactNode;
}

/**
 * A dropdown menu with search/filter, grouped checkbox items,
 * and optional select-all controls.
 */
export const FilterableCheckboxGroupDropdown: React.FC<FilterableCheckboxGroupDropdownProps> = ({
	groups,
	onCheckedChange,
	onToggleAll,
	buttonContent,
	buttonLabel,
	buttonVariant = "outline",
	buttonSize = "sm",
	buttonClassName,
	contentAlign = "end",
	contentClassName,
	loading = false,
	searchPlaceholder = "Search...",
	renderItem,
}) => {
	const [search, setSearch] = React.useState("");
	const filteredGroups = React.useMemo(() => {
		const term = search.toLowerCase();
		return groups
			.map(group => {
				const groupMatches = group.name.toLowerCase().includes(term);
				const filteredItems = group.items.filter(item => item.name.toLowerCase().includes(term));
				return groupMatches ? group : { ...group, items: filteredItems };
			})
			.filter(group => group.items.length > 0);
	}, [groups, search]);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<div className="flex flex-col space-y-1">
					{buttonLabel && <span className="text-xs font-medium text-muted-foreground">{buttonLabel}</span>}

					<Button variant={buttonVariant} size={buttonSize} className={buttonClassName} disabled={loading}>
						{buttonContent}
					</Button>
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent align={contentAlign} className={contentClassName}>
				<div className="flex flex-col max-h-[400px]">
					<div className="sticky top-0 bg-white z-10 p-2 border-b border-gray-200">
						<div
							onKeyDown={e => e.stopPropagation()}
							onKeyUp={e => e.stopPropagation()}
							onKeyPress={e => e.stopPropagation()}
						>
							<SearchInput
								value={search}
								onChange={setSearch}
								placeholder={searchPlaceholder}
								className="w-full"
								autoFocus
							/>
						</div>
					</div>
					<div className="overflow-y-auto px-2 py-1 flex-grow">
						{filteredGroups.map((group, idx) => (
							<div key={group.name}>
								<DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">
									{group.name}
								</DropdownMenuLabel>
								{group.items.map(item => (
									<DropdownMenuCheckboxItem
										key={item.id}
										checked={item.checked}
										onCheckedChange={checked => onCheckedChange(item.id, checked as boolean)}
										onSelect={e => e.preventDefault()}
									>
										{renderItem ? renderItem(item) : item.name}
									</DropdownMenuCheckboxItem>
								))}
								{onToggleAll && idx < filteredGroups.length - 1 && <DropdownMenuSeparator />}
							</div>
						))}
					</div>
					{onToggleAll && (
						<div className="sticky bottom-0 bg-white z-10 p-2 border-t border-gray-200">
							<div className="flex gap-2">
								<Button
									variant="ghost"
									className="w-full text-left text-sm px-2 py-1 h-auto"
									onClick={() => onToggleAll(true)}
								>
									Select All
								</Button>
								<Button
									variant="ghost"
									className="w-full text-left text-sm px-2 py-1 h-auto"
									onClick={() => onToggleAll(false)}
								>
									Unselect All
								</Button>
							</div>
						</div>
					)}
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
